import { c as _c } from "react/compiler-runtime";
import { type MarketParams, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { type BlueActions, DEFAULT_SUPPLY_TARGET_UTILIZATION, type VaultV2BluePublicAllocatorOptions, type VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type Address, isAddressEqual } from 'viem';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useLatestBlock } from '@/hooks/useLatestBlock';
import { useMarketPublicAllocatorVaults } from '@/hooks/useMarketPublicAllocatorVaults';
import { shouldRetryReallocationData } from './reallocationRetry';
const haveSameVaultSet = (left: readonly Address[], right: readonly Address[]) => left.length === right.length && left.every(address => right.some(candidate => isAddressEqual(candidate, address)));
const resolveReallocationError = ({
  isCandidateScope,
  candidateError,
  isReady,
  plannerError,
  reallocationDataError,
  blockError
}: {
  isCandidateScope: boolean;
  candidateError: Error | null | undefined;
  isReady: boolean;
  plannerError: Error | undefined;
  reallocationDataError: Error | null;
  blockError: Error | null;
}) => isCandidateScope && candidateError != null ? candidateError : isReady ? plannerError ?? reallocationDataError ?? blockError ?? undefined : undefined;
type ReallocationData = Awaited<ReturnType<BlueActions['getVaultV2BlueReallocationData']>>;
const planBlueReallocations = ({
  market,
  reallocationData,
  options,
  operation,
  amount
}: {
  market: BlueActions;
  reallocationData: ReallocationData;
  options: VaultV2BluePublicAllocatorOptions;
  operation: 'borrow' | 'withdraw';
  amount: bigint;
}) => market.getVaultV2BlueReallocations({
  reallocationData,
  options: {
    ...options,
    operation: {
      type: operation,
      amount
    }
  }
}).reallocations;
const maxToUtilization = ({
  reallocationData,
  marketId,
  snapshotTimestamp,
  operation
}: {
  reallocationData: ReallocationData;
  marketId: MarketParams['id'];
  snapshotTimestamp: bigint;
  operation: 'borrow' | 'withdraw';
}) => {
  // v6 fixes the supply-target trigger at `DEFAULT_SUPPLY_TARGET_UTILIZATION` (90%); the
  // per-market override the BFF used to supply is no longer configurable. Local-market only
  // (no reallocation), so `getMarket` — not `getAvailableLiquidityToUtilization`, which folds
  // friendly reallocations back in.
  const targetMarket = reallocationData.getMarket(marketId).accrueInterest(snapshotTimestamp);
  return operation === 'borrow' ? targetMarket.getBorrowToUtilization(DEFAULT_SUPPLY_TARGET_UTILIZATION) : targetMarket.getWithdrawToUtilization(DEFAULT_SUPPLY_TARGET_UTILIZATION);
};
const publicReallocationLiquidity = ({
  reallocationData,
  marketId,
  options
}: {
  reallocationData: ReallocationData;
  marketId: MarketParams['id'];
  options: VaultV2BluePublicAllocatorOptions | undefined;
}) =>
// Aggressive max: raise the source-utilization ceiling to 100% (WAD) so the displayed MAX
// reflects every valid source withdrawal, matching the amount-specific plan's fallback.
reallocationData.getPublicReallocationLiquidity(marketId, {
  ...options,
  maxWithdrawalUtilization: MathLib.WAD
});
export interface UseMarketReallocationsParams {
  /** Target market params (`client.morpho.blue(marketParams, chainId)`). */
  marketParams: MarketParams | undefined;
  /** Operation whose post-state may need a public-allocator reallocation. */
  operation: 'borrow' | 'withdraw';
  /** Asset amount the action will request. */
  amount: bigint;
  /** Gate the whole computation off (e.g. flow flag or no actionable input). */
  enabled: boolean;
}

/**
 * Public-allocator shared liquidity for market-actions-v2 borrow and loan-asset withdraw,
 * using `market.publicAllocatorVaults` for candidate discovery and
 * morpho-sdk as the single source of truth for reallocation planning.
 *
 * Two stages, mirroring the SDK's own split:
 * - `getVaultV2BlueReallocationData` — the expensive on-chain fan-out (target market + every
 *   source Vault V2 accrual tree + allocator config), pinned to a recent block so all rows share
 *   one epoch. Amount-independent, so it is **not** keyed on `amount` — typing in the input never
 *   re-triggers the RPC fetch; only a new block does.
 * - `getVaultV2BlueReallocations` — a cheap, synchronous derivation recomputed per amount. It
 *   throws (`InsufficientSharedLiquidityError` / allocator-config errors) rather than returning a
 *   partial set. The error is returned to consumers so a withdraw that depends on shared liquidity
 *   cannot silently fall back to an underfunded local-only bundle.
 *
 * The resulting `reallocations` feed `borrow`, `supplyCollateralBorrow`, or `withdraw`. v6
 * BluePublicAllocator penalties are accounted for inside BlueBundlesV1 (loan-token `penalty`),
 * with no native `fee` added to `tx.value`.
 *
 * Candidate planning is gated on a non-null `publicAllocator` from
 * `useMarketPublicAllocatorVaults`. `reallocatableVaults` is intentionally omitted because the
 * SDK defaults to every vault in the reallocation data, which is exactly the discovered list.
 */
export function useMarketReallocations(t0) {
  const $ = _c(63);
  const {
    marketParams,
    operation,
    amount,
    enabled
  } = t0;
  const {
    viemClient,
    targetChainId,
    account
  } = useWeb3Context();
  const t1 = enabled && !!marketParams && account.isConnected;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const candidates = useMarketPublicAllocatorVaults(targetChainId, marketParams?.id, t2);
  const {
    vaultAddresses
  } = candidates;
  let t3;
  if ($[2] !== marketParams || $[3] !== targetChainId || $[4] !== viemClient) {
    t3 = marketParams ? viemClient.morpho.blue(marketParams, targetChainId) : undefined;
    $[2] = marketParams;
    $[3] = targetChainId;
    $[4] = viemClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const market = t3;
  const hasCandidates = !candidates.isPending && candidates.publicAllocator != null && vaultAddresses.length > 0;
  const isCandidateScope = enabled && !!market && account.isConnected;
  const isReady = isCandidateScope && hasCandidates;
  let t4;
  if ($[6] !== isReady) {
    t4 = {
      enabled: isReady
    };
    $[6] = isReady;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: block,
    error: blockError,
    isLoading: isBlockLoading
  } = useLatestBlock(targetChainId, t4);
  const t5 = marketParams?.id;
  const t6 = block?.number;
  const t7 = block?.timestamp;
  let t8;
  if ($[8] !== market || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== targetChainId || $[13] !== vaultAddresses) {
    t8 = ["market-reallocation-data", t5, targetChainId, t6, t7, vaultAddresses, market];
    $[8] = market;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = targetChainId;
    $[13] = vaultAddresses;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== block || $[16] !== market || $[17] !== vaultAddresses) {
    t9 = async () => {
      if (block?.number == null) {
        throw new Error("Missing block for reallocation data");
      }
      const reallocationData = await market.getVaultV2BlueReallocationData({
        vaultAddresses,
        block: {
          number: block.number,
          timestamp: block.timestamp
        }
      });
      return {
        reallocationData,
        blockTimestamp: block.timestamp
      };
    };
    $[15] = block;
    $[16] = market;
    $[17] = vaultAddresses;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = isReady && block?.number != null;
  let t11;
  if ($[19] !== marketParams?.id || $[20] !== targetChainId || $[21] !== vaultAddresses) {
    t11 = (previousData, previousQuery) => {
      if (previousData == null || !previousQuery) {
        return;
      }
      const [, previousMarketId, previousChainId,,, previousVaultAddresses] = previousQuery.queryKey;
      return previousMarketId === marketParams?.id && previousChainId === targetChainId && Array.isArray(previousVaultAddresses) && haveSameVaultSet(previousVaultAddresses as Address[], vaultAddresses) ? previousData : undefined;
    };
    $[19] = marketParams?.id;
    $[20] = targetChainId;
    $[21] = vaultAddresses;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t8 || $[26] !== t9) {
    t12 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10,
      staleTime: Infinity,
      retry: shouldRetryReallocationData,
      gcTime: 20000,
      placeholderData: t11
    };
    $[23] = t10;
    $[24] = t11;
    $[25] = t8;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const {
    data: snapshot,
    error: reallocationDataError,
    isLoading: isReallocationDataLoading
  } = useQuery(t12);
  const reallocationData_0 = snapshot?.reallocationData;
  const snapshotTimestamp = snapshot?.blockTimestamp;
  let t13;
  if ($[28] !== isReady || $[29] !== snapshotTimestamp) {
    t13 = isReady && snapshotTimestamp != null ? {
      enabled: true,
      timestamp: snapshotTimestamp
    } : undefined;
    $[28] = isReady;
    $[29] = snapshotTimestamp;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const options = t13;
  let t14;
  bb0: {
    if (!isReady || !market || !reallocationData_0 || !options || amount <= ZERO) {
      let t15;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {
          reallocations: undefined,
          error: undefined
        };
        $[31] = t15;
      } else {
        t15 = $[31];
      }
      t14 = t15;
      break bb0;
    }
    ;
    try {
      let t16;
      if ($[32] !== amount || $[33] !== market || $[34] !== operation || $[35] !== options || $[36] !== reallocationData_0) {
        t16 = planBlueReallocations({
          market,
          reallocationData: reallocationData_0,
          options,
          operation,
          amount
        });
        $[32] = amount;
        $[33] = market;
        $[34] = operation;
        $[35] = options;
        $[36] = reallocationData_0;
        $[37] = t16;
      } else {
        t16 = $[37];
      }
      let t17;
      if ($[38] !== t16) {
        t17 = {
          reallocations: t16,
          error: undefined
        };
        $[38] = t16;
        $[39] = t17;
      } else {
        t17 = $[39];
      }
      t14 = t17;
    } catch (t15) {
      const error = t15;
      let t16;
      if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {
          reallocations: undefined,
          error: error instanceof Error ? error : new Error(String(error))
        };
        $[40] = t16;
      } else {
        t16 = $[40];
      }
      t14 = t16;
    }
  }
  const reallocationResult = t14;
  let t15;
  bb1: {
    if (!isReady || !reallocationData_0 || !marketParams || snapshotTimestamp == null) {
      t15 = undefined;
      break bb1;
    }
    try {
      let t16;
      if ($[41] !== marketParams.id || $[42] !== operation || $[43] !== reallocationData_0 || $[44] !== snapshotTimestamp) {
        t16 = maxToUtilization({
          reallocationData: reallocationData_0,
          marketId: marketParams.id,
          snapshotTimestamp,
          operation
        });
        $[41] = marketParams.id;
        $[42] = operation;
        $[43] = reallocationData_0;
        $[44] = snapshotTimestamp;
        $[45] = t16;
      } else {
        t16 = $[45];
      }
      t15 = t16;
    } catch {
      t15 = undefined;
    }
  }
  const amountWithoutReallocation = t15;
  let t16;
  bb2: {
    if (!isReady || !reallocationData_0 || !marketParams || snapshotTimestamp == null) {
      t16 = undefined;
      break bb2;
    }
    try {
      let t17;
      if ($[46] !== marketParams.id || $[47] !== options || $[48] !== reallocationData_0) {
        t17 = publicReallocationLiquidity({
          reallocationData: reallocationData_0,
          marketId: marketParams.id,
          options
        });
        $[46] = marketParams.id;
        $[47] = options;
        $[48] = reallocationData_0;
        $[49] = t17;
      } else {
        t17 = $[49];
      }
      t16 = t17;
    } catch {
      t16 = undefined;
    }
  }
  const maxReallocatable = t16;
  const t17 = reallocationResult.reallocations;
  let t18;
  if ($[50] !== blockError || $[51] !== candidates.error || $[52] !== isCandidateScope || $[53] !== isReady || $[54] !== reallocationDataError || $[55] !== reallocationResult.error) {
    t18 = resolveReallocationError({
      isCandidateScope,
      candidateError: candidates.error,
      isReady,
      plannerError: reallocationResult.error,
      reallocationDataError,
      blockError
    });
    $[50] = blockError;
    $[51] = candidates.error;
    $[52] = isCandidateScope;
    $[53] = isReady;
    $[54] = reallocationDataError;
    $[55] = reallocationResult.error;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const t19 = isCandidateScope && candidates.isPending || isReady && (isBlockLoading || isReallocationDataLoading);
  let t20;
  if ($[57] !== amountWithoutReallocation || $[58] !== maxReallocatable || $[59] !== reallocationResult.reallocations || $[60] !== t18 || $[61] !== t19) {
    t20 = {
      reallocations: t17,
      reallocationError: t18,
      isLoading: t19,
      amountWithoutReallocation,
      maxReallocatable
    };
    $[57] = amountWithoutReallocation;
    $[58] = maxReallocatable;
    $[59] = reallocationResult.reallocations;
    $[60] = t18;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  return t20;
}